import { useCallback, useRef } from 'react'

/**
 * Creates a debounced setter function that batches updates using a ref-based approach
 * This prevents unnecessary re-renders and bundles the state logic into a single hook
 */
export const useDebouncedSetter = <T>(setter: (value: T) => void, delay: number = 300) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedSetter = useCallback(
    (value: T) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        setter(value)
        timeoutRef.current = null
      }, delay)
    },
    [setter, delay]
  )

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  return [debouncedSetter, cleanup] as const
}
